import { NormalizedVoucher, Vehicle, VoucherSource } from '@esportal/portal-service';
import React from 'react';
import { PortalContext } from '../../../../context/portal-context';
import { isVehicle } from '../../../../helpers/common-helpers';
import { formatOwnership } from '../../../../tracking/tracking-helper';
import { useTrackingManager } from '../../../../tracking/use-tracking-manager';

const getVoucherSource = (source: VoucherSource) => {
	switch (source) {
		case 'salesforce':
			return 'Voucher Salesforce';
		case 'vwData':
			return 'Voucher Data';
		default:
			return 'Voucher';
	}
};

export const useVouchersTracker = (voucher: NormalizedVoucher) => {
	const {
		source,
		card: { title },
		campaign,
		layer,
		vin,
	} = voucher;
	const sectionName = `Benefits - ${getVoucherSource(source)}`;
	const campaignValue = campaign ? ` - Campaign: ${campaign}` : '';
	const contentIdValue = `${sectionName} - ${title}${campaignValue}`;

	const trackingManager = useTrackingManager();
	const { portalService } = React.useContext(PortalContext);

	const getTrackingVehicles = React.useCallback((): Vehicle[] => {
		if (vin) {
			const foundVehicle = portalService.getVehicleOrOrderByVin(vin).getValue();
			if (isVehicle(foundVehicle)) {
				return [foundVehicle];
			}
		}

		const userVehicles = portalService.getPersonVehicles().getValue();
		return userVehicles?.vehicles ?? [];
	}, [portalService, vin]);

	const trackCardClick = () => {
		const customContentIdValue = `${sectionName} ${layer ? '- Open Layer' : ''} - ${title}${campaignValue}`;

		trackingManager.trackClubVWButtonClick(
			customContentIdValue,
			{ contentId: customContentIdValue },
			{
				vehicle: formatOwnership(getTrackingVehicles()),
			},
		);
	};

	const trackLayerLoad = React.useCallback(() => {
		trackingManager.trackClubVWLayerLoad(
			{ contentId: contentIdValue },
			{ vehicle: formatOwnership(getTrackingVehicles()) },
		);
	}, [getTrackingVehicles, trackingManager, contentIdValue]);

	const trackLayerCTAClick = (ctaLabel: string) => {
		const customContentIdValue = `${ctaLabel} - ${contentIdValue}`;
		trackingManager.trackClubVWButtonClick(
			customContentIdValue,
			{ contentId: customContentIdValue },
			{
				vehicle: formatOwnership(getTrackingVehicles()),
			},
		);
	};

	const trackLayerCloseClick = () => {
		trackingManager.trackClubVWLayerCloseClick(contentIdValue, { contentId: contentIdValue });
	};

	// VWBank tracking functions

	const trackVWFSLayerLoad = React.useCallback(() => {
		const customContentIdValue = `${contentIdValue} - Warning exit`;
		trackingManager.trackClubVWLayerLoad(
			{ contentId: customContentIdValue },
			{ vehicle: formatOwnership(getTrackingVehicles()) },
		);
	}, [getTrackingVehicles, trackingManager, contentIdValue]);

	const trackVWFSLayerCTAClick = (ctaLabel: string) => {
		const customContentIdValue = `${ctaLabel} - ${contentIdValue} - Warning exit`;
		trackingManager.trackClubVWButtonClick(
			customContentIdValue,
			{ contentId: customContentIdValue },
			{
				vehicle: formatOwnership(getTrackingVehicles()),
			},
		);
	};

	const trackVWFSLayerCloseClick = () => {
		const customContentIdValue = `${contentIdValue} - Warning exit`;
		trackingManager.trackClubVWLayerCloseClick(customContentIdValue, { contentId: customContentIdValue });
	};

	return {
		trackCardClick,
		trackLayerLoad,
		trackLayerCTAClick,
		trackLayerCloseClick,
		trackVWFSLayerLoad,
		trackVWFSLayerCTAClick,
		trackVWFSLayerCloseClick,
	};
};
